import { useCallback, useEffect, useState } from 'react';
import { pushErrorFromAPI } from 'src/utilities/notifications';
import { getDinerCounts } from 'src/components/selfService/features/LineOfCredit/LineOfCreditLanding.utils';

type DataType = Record<string, number>;

type UseFetchDinerCountPerGroupReturn = {
  data?: DataType;
  isLoading?: boolean;
  refetch: () => void;
};

export const useFetchDinerCountPerGroup = (
  groupIds: string[],
  corpId: string
): UseFetchDinerCountPerGroupReturn => {
  const [isLoading, setIsLoading] = useState<boolean>();
  const [data, setData] = useState<DataType>();
  const [error, setError] = useState();
  const hasNewGroupId = data
    ? groupIds.some((groupId) => data[groupId] === undefined)
    : true;

  const fetchDinerCountPerGroup = useCallback(async () => {
    if (!groupIds.length || isLoading) {
      return;
    }
    try {
      setError(null);
      setIsLoading(true);
      const responseData = await getDinerCounts(corpId, groupIds);

      setData(
        responseData.object.group_counts.reduce<DataType>(
          (acc, { group_id, diner_count }) => {
            acc[group_id] = diner_count;
            return acc;
          },
          {}
        )
      );
      setIsLoading(false);
    } catch (error) {
      setError(error);
      pushErrorFromAPI(error);
    } finally {
      setIsLoading(false);
    }
  }, [groupIds, isLoading, corpId]);

  useEffect(() => {
    if (!error && hasNewGroupId && !isLoading) {
      fetchDinerCountPerGroup();
    }
  }, [error, fetchDinerCountPerGroup, hasNewGroupId, isLoading]);

  return {
    data,
    isLoading,
    refetch: fetchDinerCountPerGroup,
  };
};
